import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
} from 'platform/components';
import {Show, VStack} from 'platform/foundation';
import {z} from 'zod';

import {isNil, isNotNil} from 'ramda';

import {
  ServiceItemMoveReason,
  usePatchServiceItemMoveReasonSetDefaultMutation,
  usePatchServiceItemMoveReasonUnsetDefaultMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

interface MoveReasonDefaultFormProps extends RequiredTestIdProps {
  moveReasons: ServiceItemMoveReason[];
}

const defaultTemplateSchema = z.object({
  codelistId: z.string().min(1),
});

type DefaultTemplateFormValues = z.infer<typeof defaultTemplateSchema>;

export function MoveReasonDefaultForm(props: MoveReasonDefaultFormProps) {
  const [setDefault] = usePatchServiceItemMoveReasonSetDefaultMutation();
  const [unsetDefault, {isLoading: isUnsetLoading}] =
    usePatchServiceItemMoveReasonUnsetDefaultMutation();

  const defaultMoveReason = props.moveReasons.find((moveReason) => moveReason.isDefault);

  const handleSetDefault: FormSubmitHandler<DefaultTemplateFormValues> = (values) =>
    setDefault({codelistId: values.codelistId})
      .unwrap()
      .then(closeCurrentDialog)
      .catch(handleApiError);

  const handleUnsetDefault = () => {
    if (isNil(defaultMoveReason)) {
      return;
    }

    unsetDefault({codelistId: defaultMoveReason.id})
      .unwrap()
      .then(closeCurrentDialog)
      .catch(handleApiError);
  };

  return (
    <Form<DefaultTemplateFormValues>
      experimentalZodSchema={defaultTemplateSchema}
      defaultValues={{codelistId: defaultMoveReason?.id ?? ''}}
      onSubmit={handleSetDefault}
    >
      {(control) => (
        <VStack spacing={4}>
          <FormField
            control={control}
            name="codelistId"
            type="choice"
            options={props.moveReasons.map((moveReason) => ({
              value: moveReason.id,
              label: moveReason.name,
            }))}
            label={i18n.t('entity.vehicle.labels.template')}
            isRequired
            data-testid={suffixTestId('template', props)}
          />
          <ButtonGroup align="right">
            <Button
              title={i18n.t('general.actions.cancel')}
              variant="secondary"
              onClick={closeCurrentDialog}
              data-testid={suffixTestId('cancel', props)}
            />
            <Show when={isNotNil(defaultMoveReason)}>
              <Button
                title={i18n.t('general.actions.unsetDefault')}
                variant="secondary"
                isLoading={isUnsetLoading}
                onClick={handleUnsetDefault}
                data-testid={suffixTestId('unsetDefault', props)}
              />
            </Show>
            <FormButton
              control={control}
              title={i18n.t('general.actions.setAsDefault')}
              type="submit"
              data-testid={suffixTestId('setDefault', props)}
            />
          </ButtonGroup>
        </VStack>
      )}
    </Form>
  );
}
